import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle, AlertTriangle, XCircle, Info, X } from 'lucide-react';

// ============================================================================
// Toast system — context + portal-mounted stack + useToast() hook.
//
// Replaces the ~5 different inline `msg` divs scattered across pages with a
// single, consistent surface. Each toast auto-dismisses after `duration` ms
// (default 4s for info/success, 6s for warnings/errors so the user has time
// to read them). Stack is hard-capped at 5 — older toasts age off when a
// new one would push past the limit.
// ============================================================================

const ToastContext = createContext(null);

const VARIANT = {
  success: { icon: CheckCircle,    color: 'var(--green)',      bg: 'rgba(13,19,32,0.96)', border: 'rgba(34,197,94,0.40)',  glow: '0 8px 28px rgba(34,197,94,0.18)' },
  error:   { icon: XCircle,        color: 'var(--red)',        bg: 'rgba(13,19,32,0.96)', border: 'rgba(239,68,68,0.40)',  glow: '0 8px 28px rgba(239,68,68,0.18)' },
  warning: { icon: AlertTriangle,  color: 'var(--amber)',      bg: 'rgba(13,19,32,0.96)', border: 'rgba(245,158,11,0.40)', glow: '0 8px 28px rgba(245,158,11,0.18)' },
  info:    { icon: Info,           color: 'var(--accent-soft)', bg: 'rgba(13,19,32,0.96)', border: 'rgba(99,109,240,0.40)',  glow: '0 8px 28px rgba(99,109,240,0.18)' },
};

let _seq = 0;
const nextId = () => ++_seq;

export function ToastProvider({ children, max = 5 }) {
  const [toasts, setToasts] = useState([]);
  const timersRef = useRef(new Map());

  const dismiss = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
    const t = timersRef.current.get(id);
    if (t) { clearTimeout(t); timersRef.current.delete(id); }
  }, []);

  const push = useCallback((toast) => {
    const id = nextId();
    const variant = toast.variant || 'info';
    const duration = toast.duration ?? (variant === 'error' || variant === 'warning' ? 6000 : 4000);
    setToasts(prev => {
      const next = [...prev, { id, variant, ...toast }];
      // Drop the oldest when over the cap.
      while (next.length > max) {
        const oldest = next.shift();
        const oldTimer = timersRef.current.get(oldest.id);
        if (oldTimer) { clearTimeout(oldTimer); timersRef.current.delete(oldest.id); }
      }
      return next;
    });
    if (duration > 0) {
      const timer = setTimeout(() => dismiss(id), duration);
      timersRef.current.set(id, timer);
    }
    return id;
  }, [dismiss, max]);

  // Helpers — every page can do `toast.success('Trade executed')` instead of
  // assembling an object literal.
  const api = useRef(null);
  if (!api.current) {
    api.current = {
      push,
      dismiss,
      success: (message, opts = {}) => push({ variant: 'success', message, ...opts }),
      error:   (message, opts = {}) => push({ variant: 'error',   message, ...opts }),
      warning: (message, opts = {}) => push({ variant: 'warning', message, ...opts }),
      info:    (message, opts = {}) => push({ variant: 'info',    message, ...opts }),
    };
  }

  useEffect(() => () => {
    for (const t of timersRef.current.values()) clearTimeout(t);
    timersRef.current.clear();
  }, []);

  return (
    <ToastContext.Provider value={api.current}>
      {children}
      <ToastContainer toasts={toasts} dismiss={dismiss} />
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
  return ctx;
}

// ── Container (portal) ────────────────────────────────────────────────────
function ToastContainer({ toasts, dismiss }) {
  if (typeof document === 'undefined') return null; // SSR safety
  return createPortal(
    <div
      role="region"
      aria-label="Notifications"
      style={{
        position: 'fixed',
        top: 16, right: 16,
        zIndex: 9999,
        display: 'flex', flexDirection: 'column', gap: 8,
        pointerEvents: 'none', // children re-enable so the empty space stays clickable
        maxWidth: 'calc(100vw - 32px)', width: 'min(380px, calc(100vw - 32px))',
      }}
    >
      {toasts.map(t => (
        <ToastItem key={t.id} toast={t} onDismiss={() => dismiss(t.id)} />
      ))}
      <style>{`
        @media (max-width: 480px) {
          [aria-label="Notifications"] {
            top: auto !important;
            bottom: calc(90px + env(safe-area-inset-bottom)) !important;
            right: 12px !important;
            left: 12px !important;
            width: auto !important;
          }
        }
      `}</style>
    </div>,
    document.body
  );
}

function ToastItem({ toast, onDismiss }) {
  const v = VARIANT[toast.variant] || VARIANT.info;
  const Icon = v.icon;
  return (
    <div
      role={toast.variant === 'error' ? 'alert' : 'status'}
      className="animate-slide-right"
      style={{
        pointerEvents: 'auto',
        display: 'flex', alignItems: 'flex-start', gap: 10,
        padding: '12px 14px',
        borderRadius: 'var(--r-md)',
        background: v.bg,
        border: `1px solid ${v.border}`,
        boxShadow: v.glow,
        backdropFilter: 'blur(12px) saturate(140%)',
        WebkitBackdropFilter: 'blur(12px) saturate(140%)',
      }}
    >
      <Icon size={16} strokeWidth={2.4} color={v.color} style={{ flexShrink: 0, marginTop: 1 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        {toast.title && (
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 2 }}>
            {toast.title}
          </div>
        )}
        <div style={{ fontSize: 12, color: toast.title ? 'var(--text-secondary)' : 'var(--text-primary)', lineHeight: 1.45, wordBreak: 'break-word' }}>
          {toast.message}
        </div>
        {toast.action && (
          <button
            onClick={() => { toast.action.onClick?.(); onDismiss(); }}
            style={{
              marginTop: 6, padding: 0, background: 'transparent', border: 'none',
              color: v.color, fontSize: 11, fontWeight: 600, cursor: 'pointer',
              fontFamily: 'inherit', textDecoration: 'underline', textUnderlineOffset: 2,
            }}>
            {toast.action.label}
          </button>
        )}
      </div>
      <button
        onClick={onDismiss}
        aria-label="Dismiss"
        style={{
          background: 'transparent', border: 'none', color: 'var(--text-muted)',
          cursor: 'pointer', padding: 2, flexShrink: 0,
        }}>
        <X size={13} />
      </button>
    </div>
  );
}

export default ToastProvider;
